import { Button, Card, PageTitle, Spinner, Tab, useIntl } from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { EditProvisionEvent, ProvisionEventEquipment, ProvisionType as ProvisionType } from "features/provisioning/provisions/models/provision-models"
import { FC, useMemo, useState, useCallback } from "react"
import customerStore from "features/organizations/customers/store/customer-store"
import provisionEventStore from "features/provisioning/provisions/stores/provision-event-store"
import { SidebarSelection } from "./provision-event-sidebar-selection"
import { useNavigate, useParams } from "react-router-dom"
import linerMaterialStore from "features/provisioning/liner-material-library/stores/liner-material-store"
import styles from "./provision-event-asset-editor.module.css"
import { ProvisionEventsSVGViewer } from "../provision-events-svg-viewer"
import sensorStockStore from "features/provisioning/provisions/stores/sensor-stock-store"
import { normaliseEquipmentList, resolveEquipmentElements } from "../utils/provision-utils"
import { Customer } from "features/organizations/customers/models/customer-models"
import { Site } from "features/organizations/sites/models/sites-models"
import { Asset } from "features/organizations/assets/models/asset-models"
import { convertDateToDDMMMYY, convertDateToDDMMMYYYY } from "components/utils/date-formatter-utils"
import { provisionEventsLinkGenerator } from "../../provisions-utils/provision-events-link-generator"
import { EquipmentSelectedCbType, EquipmentType, SelectedElement } from "../../provisions-utils/provision-types"

const inlineStyles = `
.width50 {
    width: 50%
}
.tab-align-in-card-header {
    padding-bottom: 0px !important;
    padding-top: 22px !important;
}
.list-align-in-card-content {
    padding-left: 0px !important;
    padding-right: 0px !important;
}

.wrapper> div {
    width: 100% !important;
    height: 100% !important;
}

.wrapper svg {
    width: 100% !important;
    height: 100% !important;
}
`

type TabContentProps = {
    indexAssetSection: number
    customer: Customer
    site: Site
    asset: Asset
    eventId?: string
}

const TabContent = ({ indexAssetSection, customer, site, asset, eventId }: TabContentProps) => {
    const navigate = useNavigate()
    const assetSections = asset.assetSections
    const assetSectionLabels = assetSections.map(s => s.name)

    return (
        <Tab
            as='div'
            data-cy='tab'
            activeIndex={indexAssetSection}
            onTabChange={(e, d) => {
                const nextIndex = Number(d.activeIndex)
                const nextAssetSection = assetSections[nextIndex]
                if (nextAssetSection) {
                    navigate(provisionEventsLinkGenerator(customer, site, asset, nextAssetSection, { id: eventId }, "/edit"))
                }
            }}
            menu={{
                pointing: true,
                secondary: true,
            }}
            style={{ width: "100%" }}
            panes={assetSectionLabels.map(asl => {
                return { menuItem: asl }
            })}
        />
    )
}

export const ProvisionEventEditor: FC = observer(() => {
    const { customerId, siteId, assetId, assetSectionId, provisionEventId } = useParams()

    if (!customerId) return null
    if (!siteId) return null
    if (!assetId) return null
    if (!provisionEventId) return null

    const { formatMessage } = useIntl()
    const navigate = useNavigate()

    const { saveProvisionEvent } = provisionEventStore
    const { linerMaterials } = linerMaterialStore
    const { stockSensors } = sensorStockStore

    const { customerMap, siteMap, assetMap, assetSectionMap } = customerStore
    // Other provision events
    const nonEditorProvisionEvents = provisionEventStore.provisionEventsDetail.filter(p => p.id !== provisionEventId)
    // Edited provision event
    const editorProvisionEvent = provisionEventStore.provisionEventsDetail.find(p => p.id === provisionEventId)

    if (!editorProvisionEvent) throw new Error("Provision event not found")

    const activeEquipmentProvisions = useMemo(() => {
        return nonEditorProvisionEvents
            .map(p => {
                const elements = resolveEquipmentElements(p)
                return normaliseEquipmentList(elements)
            })
            .flatMap(x => x)
    }, [nonEditorProvisionEvents])

    const [isSaveInProgress, setIsSaveInProgress] = useState(false)
    const [selectedEquipment, setSelectedEquipment] = useState<SelectedElement[]>(resolveEquipmentElements(editorProvisionEvent))
    const [activeEquipmentType, setActiveEquipmentType] = useState<EquipmentType>("Liner")

    const customer = customerMap[customerId]
    const site = siteMap[siteId]
    const asset = assetMap[assetId]

    const assetSections = asset.assetSections

    const indexAssetSection = assetSections?.findIndex(s => s.id === assetSectionId) ?? 0
    const activeAssetSection = assetSectionId ? assetSectionMap[assetSectionId] : assetSections[0]

    const handleProvisionEventSaveOpen = useCallback(async () => {
        setIsSaveInProgress(true)
        const outgoingEvent: EditProvisionEvent = {
            id: editorProvisionEvent.id,
            assetId: editorProvisionEvent.assetId,
            assetSectionId: activeAssetSection.id,
            description: editorProvisionEvent.description,
            eventDate: convertDateToDDMMMYYYY(editorProvisionEvent.eventDate),
            type: editorProvisionEvent.type,
            liners: selectedEquipment
                .filter(l => l.type === "Liner")
                .map(l => {
                    if (!l.equipmentOptionId) {
                        throw new Error(`Material id for liner location ${l.linerSvgReference} is missing`)
                    }
                    return {
                        equipmentId: l.equipmentId,
                        linerLocationReference: l.linerSvgReference,
                        materialId: l.equipmentOptionId,
                        provisionType: l.provisionType,
                    }
                }),
            sensors: selectedEquipment
                .filter(s => s.type === "Sensor")
                .map(s => {
                    if (!s.equipmentOptionId) {
                        throw new Error(`Sensor serialnumber for sensor location ${s.sensorSvgReference} is missing`)
                    }
                    return {
                        equipmentId: s.equipmentId,
                        linerLocationReference: s.linerSvgReference,
                        sensorLocationReference: s.sensorSvgReference,
                        serialNumber: s.equipmentOptionId,
                        provisionType: s.provisionType,
                    }
                }),
        }
        await saveProvisionEvent(outgoingEvent)
        setIsSaveInProgress(false)
        navigate(provisionEventsLinkGenerator(customer, site, asset))
    }, [editorProvisionEvent, selectedEquipment])

    const handleLeavePage = useCallback(() => navigate(provisionEventsLinkGenerator(customer, site, asset)), [])

    const pageTitle = useMemo(() => {
        if (editorProvisionEvent?.eventDate) {
            return `${convertDateToDDMMMYY(editorProvisionEvent.eventDate)} / ${editorProvisionEvent.description}`
        }
    }, [editorProvisionEvent])

    const handleEquipmentElementSelected = useCallback<EquipmentSelectedCbType>(
        (
            equipmentType: EquipmentType,
            linerRefenceIdsList: string[],
            sensorReferenceId: string | undefined,
            isSelectAll: boolean,
            materialId?: string,
        ) => {
            const incomingElements = linerRefenceIdsList.map(
                referenceId =>
                    ({
                        type: equipmentType,
                        linerSvgReference: referenceId,
                        sensorSvgReference: sensorReferenceId,
                        equipmentOptionId: materialId ?? "",
                        provisionType: editorProvisionEvent.type,
                        provisionDate: editorProvisionEvent.eventDate,
                    } as SelectedElement),
            )

            const hasActiveEquipment = (equipmentEvents: ProvisionEventEquipment[]) => {
                const commissionEvents = equipmentEvents.filter(p => p.type === ProvisionType.Commissioned)
                commissionEvents.sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())
                const decommissionEvents = equipmentEvents.filter(p => p.type === ProvisionType.Decommissioned)
                decommissionEvents.sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())

                const latestCommissionEvent = commissionEvents[0]
                const latestdecommissionEvent = decommissionEvents[0]

                if (!latestCommissionEvent) return false
                if (!latestdecommissionEvent) return true

                return new Date(latestCommissionEvent.eventDate).getTime() > new Date(latestdecommissionEvent.eventDate).getTime()
            }

            if (editorProvisionEvent.type === ProvisionType.Decommissioned) {
                // Allow to only decommission commissioned equipment
                if (equipmentType === "Liner") {
                    var linerEvents = nonEditorProvisionEvents.filter(p => p.liners.some(l => l.linerLocation.reference === linerRefenceIdsList[0]))
                    if (!hasActiveEquipment(linerEvents)) {
                        return
                    }
                } else if (equipmentType === "Sensor") {
                    var sensorEvents = nonEditorProvisionEvents.filter(p => p.sensors.some(s => s.reference === sensorReferenceId))
                    if (!hasActiveEquipment(sensorEvents)) {
                        return
                    }
                }
            }

            const resolveSelectedElements = (elements: SelectedElement[]) => {
                if (isSelectAll) {
                    return [...selectedEquipment.filter(s => s.type === "Liner"), ...elements] // Select all is only available for Liners
                }

                const liners = selectedEquipment.filter(s => s.type === "Liner")
                const sensors = selectedEquipment.filter(s => s.type === "Sensor")

                //
                const nonIncomingLiners = liners.filter(liner =>
                    elements.filter(x => x.type === "Liner").every(x => x.linerSvgReference !== liner.linerSvgReference),
                )
                const nonIncomingSensors = sensors.filter(sensor => elements.every(x => x.sensorSvgReference !== sensor.sensorSvgReference))

                return [...nonIncomingLiners, ...nonIncomingSensors, ...elements]
            }

            const equipmentList = resolveSelectedElements(incomingElements)
            setSelectedEquipment(equipmentList)
        },
        [selectedEquipment, activeEquipmentType],
    )

    const handleRemoveElement = useCallback(
        (refId: string) => {
            const filterLiner = (s: SelectedElement) => s.linerSvgReference !== refId
            const filterSensor = (s: SelectedElement) => s.sensorSvgReference !== refId

            const sensors = selectedEquipment.filter(eq => eq.type === "Sensor")
            const liners = selectedEquipment.filter(eq => eq.type === "Liner")
            activeEquipmentType === "Liner"
                ? setSelectedEquipment([...sensors, ...liners.filter(filterLiner)])
                : setSelectedEquipment([...liners, ...sensors.filter(filterSensor)])
        },
        [selectedEquipment, activeEquipmentType],
    )

    const handleEquipmentTypeChanged = useCallback((equipmentType: EquipmentType) => setActiveEquipmentType(equipmentType), [activeEquipmentType])

    const sidebarItems = useMemo(() => {
        const resolveLabel = (element: SelectedElement) => {
            const typeSpecificLabel =
                activeEquipmentType === "Liner"
                    ? linerMaterials?.find(m => m.id == element.equipmentOptionId)?.materialReference
                    : element.equipmentOptionId ?? "unknown"

            return `${element.linerSvgReference} - ${typeSpecificLabel}`
        }
        return selectedEquipment
            .filter(s => s.type === activeEquipmentType)
            .map(element => ({
                id: activeEquipmentType === "Liner" ? element.linerSvgReference : element.sensorSvgReference ?? "",
                label: resolveLabel(element),
            }))
    }, [selectedEquipment, activeEquipmentType])

    const equipmentOptions = useMemo(() => {
        if (activeEquipmentType === "Liner") {
            const sortedMaterial = [...(linerMaterials ?? [])].sort((a, b) => a?.brand?.localeCompare(b?.brand))
            return sortedMaterial?.map(m => ({ text: m.materialReference, value: m.id })) ?? []
        }
        return stockSensors.map(s => ({ text: s.serialNumber, value: s.serialNumber }))
    }, [linerMaterials, stockSensors, activeEquipmentType])

    const normalizedEquipmentList = useMemo(() => {
        return normaliseEquipmentList([...activeEquipmentProvisions, ...selectedEquipment])
    }, [selectedEquipment, activeEquipmentProvisions])

    const renderContent = () => {
        if (!editorProvisionEvent) {
            return "No provision event found"
        }

        return (
            <>
                <div className={styles.content}>
                    <Card
                        className={styles.cardContainer}
                        header={
                            <TabContent
                                asset={asset}
                                customer={customer}
                                indexAssetSection={indexAssetSection}
                                site={site}
                                eventId={editorProvisionEvent.id}
                            />
                        }
                        headerClassName={styles.tabHeader}
                        content={
                            <ProvisionEventsSVGViewer
                                asset={asset}
                                assetSectionId={activeAssetSection.id}
                                requireEquipmentOptionToBeSelected={editorProvisionEvent.type !== ProvisionType.Decommissioned}
                                allowAllEquipmentSelection={activeEquipmentType === "Liner"}
                                selectedElements={normalizedEquipmentList}
                                onEquipmentSelected={handleEquipmentElementSelected}
                                equipmentOptions={equipmentOptions}
                                activeEquipmentType={activeEquipmentType}
                            />
                        }
                        data-cy='card-svg-content'
                    />

                    <SidebarSelection
                        listItems={sidebarItems}
                        provisionType={editorProvisionEvent.type}
                        equipmentType={activeEquipmentType}
                        onDeleteElement={handleRemoveElement}
                        onEquipmentTypeChanged={handleEquipmentTypeChanged}
                    />
                </div>
            </>
        )
    }

    return (
        <>
            <style>{inlineStyles}</style>
            <PageTitle
                title={pageTitle}
                extraItems={
                    <>
                        <Button icon='icon-arrow-left-16' labelPosition='left' onClick={handleLeavePage} subtle>
                            {formatMessage("provisionEvents.edit.back")}
                        </Button>
                        <Button icon='icon-plus-16' labelPosition='left' onClick={handleProvisionEventSaveOpen} primary>
                            {formatMessage("provisionEvents.edit.save")}
                        </Button>
                    </>
                }
            />
            {isSaveInProgress ? <Spinner text={"Saving provision event"} /> : renderContent()}
        </>
    )
})
